/* ---------------------------------------------------------------------------
 * Authored blank lines
 * ---------------------------------------------------------------------------
 * A blank line is what an author gets when they press Enter on an empty line in
 * a TipTap editor: an empty <p>. ProseMirror draws one with a trailing <br> of
 * its own (see `addTextblockHacks` in prosemirror-view), so it holds a full line
 * for as long as they are editing — but the `<p></p>` that is saved has no
 * inline content, generates no line box, and so draws nothing at all anywhere it
 * is rendered afterwards: the Yurtzeiten preview card, an Event Template on a
 * board. The blank line was always saved. It simply never appeared again.
 *
 * The repair is presentational — an empty inline-block gives the paragraph its
 * line box back, one `line-height` tall and no characters wide — but it cannot
 * be written as a bare `p:empty` rule, because by the time a design or a
 * template reaches a screen there are TWO ways a block can be empty:
 *
 *   1. the author left it blank, and it has to draw the line they left; and
 *   2. it holds nothing but a {token} the record has no value for — an event
 *      with no location, a yahrzeit with no note — which has always collapsed,
 *      and has to go on collapsing, or every unfilled field would open a gap.
 *
 * Only the first is a blank line, and the two are distinguishable only BEFORE
 * the variables are substituted. So that is where this runs: it marks case 1
 * while the markup is still exactly what was saved, and the stylesheets key off
 * the marker rather than off emptiness.
 */

/** Set on each block the author left blank. Styled per rendering surface. */
export const BLANK_LINE_ATTR = 'data-blank-line';

// The blocks an editor in this app can leave empty. A list item is serialised as
// <li><p></p></li>, so marking the paragraph inside it carries the item too.
const BLANK_LINE_SELECTOR = 'p,h1,h2,h3,h4,h5,h6';

/**
 * Mark every block the author left blank, in place. Returns how many were
 * marked, so a caller holding a string can skip re-serialising for nothing.
 *
 * Emptiness is `childNodes.length === 0` rather than `:empty` or a trimmed
 * textContent, and the difference is deliberate: ProseMirror never serialises an
 * empty text node, so a block with no child nodes at all is exactly one the
 * author left blank. A block holding only a space is NOT one — it collapses
 * inside the editor too, so both surfaces already agree about it.
 */
export const markAuthoredBlankLines = (root: Element): number => {
  let marked = 0;

  root.querySelectorAll(BLANK_LINE_SELECTOR).forEach((element) => {
    if (element.childNodes.length === 0) {
      element.setAttribute(BLANK_LINE_ATTR, 'true');
      marked += 1;
    }
  });

  return marked;
};

/**
 * The same pass over a fragment of HTML, for a caller that has no DOM yet.
 *
 * The original string is handed straight back when there is nothing to mark, so
 * a layout without a blank line in it is never rewritten by a round trip
 * through the parser.
 */
export const markAuthoredBlankLinesHtml = (html: string): string => {
  if (!html) return html;

  const parsed = new DOMParser().parseFromString(
    `<div id="blank-line-root">${html}</div>`,
    'text/html'
  );
  const root = parsed.getElementById('blank-line-root');
  if (!root) return html;

  return markAuthoredBlankLines(root) ? root.innerHTML : html;
};
